import axios from 'axios';
import { API_BASE_URL, RUNTIME_ENV, STAGING_API_BASE_URL } from '../config';

// In-memory token management. AuthContext should call these.
let accessToken = null;
export function setAccessToken(token) { accessToken = token; }
export function clearAccessToken() { accessToken = null; }
export function getAccessToken() { return accessToken; }

// Helper to persist refresh token in localStorage (already used elsewhere)
function getRefreshToken() { return localStorage.getItem('refreshToken'); }
function setRefreshToken(token) { localStorage.setItem('refreshToken', token); }
function clearRefreshToken() { localStorage.removeItem('refreshToken'); }

// Create axios instance based on environment
const http = axios.create({ 
	baseURL: RUNTIME_ENV === 'staging' ? STAGING_API_BASE_URL : API_BASE_URL 
});

// Flag to avoid multiple parallel refresh attempts
let isRefreshing = false;
let refreshPromise = null;

async function attemptRefresh() {
	if (isRefreshing) {
		console.log('🔄 Refresh already in progress, waiting...');
		return refreshPromise;
	}
	const rt = getRefreshToken();
	if (!rt) throw new Error('No refresh token available');
	
	console.log('🔄 Starting token refresh...');
	isRefreshing = true;
	refreshPromise = http.post('/api/v1/auth/refresh', { refreshToken: rt }, { skipAuthRefresh: true })
		.then(res => {
			if (res.data?.tokens) {
				setAccessToken(res.data.tokens.accessToken);
				setRefreshToken(res.data.tokens.refreshToken);
			} else if (res.data?.accessToken) { // fallback if backend returns flat
				console.log('🔄 Setting new tokens from flat structure');
				setAccessToken(res.data.accessToken);
				if (res.data.refreshToken) setRefreshToken(res.data.refreshToken);
			} else {
				throw new Error('Invalid refresh response');
			}
		})
		.catch(err => {
			console.log('🔄 Refresh failed, clearing tokens:', err.message);
			clearAccessToken();
			clearRefreshToken();
			throw err;
		})
		.finally(() => {
			isRefreshing = false;
		});
	return refreshPromise;
}

http.interceptors.request.use(cfg => {
	const token = getAccessToken();
	if (token && !cfg.headers.Authorization) {
		cfg.headers.Authorization = `Bearer ${token}`;
	}
	return cfg;
});

http.interceptors.response.use(r => r, async error => {
	const { config, response } = error;
	
	if (!response) return Promise.reject(error); // network/timeout

	// Skip if explicitly flagged (e.g., refresh endpoint itself)
	if (config?.skipAuthRefresh) return Promise.reject(error);

	// Do not attempt refresh on the login, forgot-password, verify-otp, reset-password endpoints
	const isAuthAcquisition = /\/api\/v1\/auth\/(login|forgot-password|verify-otp|reset-password)/.test(config?.url || '');

	if (response.status === 401 && !config.__isRetry && !isAuthAcquisition) {
		console.log('🔄 401 detected, attempting refresh...');
		// Only try if we actually have a refresh token
		const refreshToken = getRefreshToken();
		console.log('🔄 Refresh token available:', !!refreshToken);
		
		if (refreshToken) {
			try {
				console.log('🔄 Attempting token refresh...');
				await attemptRefresh();
				console.log('🔄 Token refresh successful, retrying original request');
				const retryCfg = { ...config, __isRetry: true };
				delete retryCfg.headers?.Authorization;
				return http.request(retryCfg);
			} catch (refreshErr) {
				console.log('🔄 Token refresh failed:', refreshErr.message);
				if (window.location.pathname !== '/') {
					window.location.href = '/';
				}
				return Promise.reject(refreshErr);
			}
		} else {
			console.log('🔄 No refresh token available, redirecting to home');
			if (window.location.pathname !== '/') {
				// Don't loop if we're already home
				if (window.location.pathname !== '/signin') // Just mostly avoiding loops, but / is main target
				window.location.href = '/';
			}
		}
	}
	return Promise.reject(error);
});

export default http;
